import { useState, useEffect } from 'react';
import type { Source, CreateSourceRequest, SourceType } from '@claude-code-insights/shared';
import { SOURCE_TYPES } from '@claude-code-insights/shared';
import { fetchSources, createSource, updateSource, deleteSource, pollSource, exportObsidian } from '../lib/api';

interface FieldConfig {
  identifier?: { label: string; placeholder: string };
  filters?: { label: string; placeholder: string };
  configSort?: { label: string; options: { value: string; label: string }[] };
  configLanguage?: { label: string; placeholder: string };
}

const SOURCE_FIELD_CONFIG: Record<SourceType, { fields: FieldConfig; implemented: boolean }> = {
  hackernews: {
    implemented: true,
    fields: {
      filters: { label: 'Search terms (comma-separated, OR logic)', placeholder: 'e.g., claude code, mcp server, claude skills' },
    },
  },
  reddit: {
    implemented: true,
    fields: {
      identifier: { label: 'Subreddit', placeholder: 'e.g., ClaudeAI' },
      configSort: {
        label: 'Sort by',
        options: [
          { value: 'hot', label: 'Hot' },
          { value: 'new', label: 'New' },
          { value: 'top', label: 'Top' },
          { value: 'rising', label: 'Rising' },
        ],
      },
      filters: { label: 'Keyword filters (comma-separated)', placeholder: 'e.g., claude code, mcp, hooks' },
    },
  },
  rss: {
    implemented: true,
    fields: {
      identifier: { label: 'Feed URL', placeholder: 'e.g., https://example.com/feed.xml or raw XML URL' },
    },
  },
  'x/twitter': { implemented: false, fields: { identifier: { label: 'Username or search query', placeholder: 'e.g., @anthropikitten' } } },
  bluesky: {
    implemented: true,
    fields: {
      filters: { label: 'Search keywords (comma-separated)', placeholder: 'e.g., claude code, anthropic, mcp server' },
    },
  },
  mastodon: {
    implemented: true,
    fields: {
      identifier: { label: 'Instance domain', placeholder: 'e.g., mastodon.social' },
      filters: { label: 'Search keywords (comma-separated)', placeholder: 'e.g., claude code, AI coding' },
    },
  },
  github: {
    implemented: true,
    fields: {
      filters: { label: 'Search keywords (space-separated)', placeholder: 'e.g., claude code mcp server' },
      configLanguage: { label: 'Language filter (optional)', placeholder: 'e.g., typescript' },
    },
  },
  discord: { implemented: false, fields: { identifier: { label: 'Channel ID', placeholder: 'e.g., 123456789' } } },
  gmail: {
    implemented: true,
    fields: {
      identifier: { label: 'Gmail search query', placeholder: 'e.g., label:claude-updates OR subject:claude code' },
    },
  },
  telegram: { implemented: true, fields: {} },
};

export function SourcesView() {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [pollingId, setPollingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const { url } = await exportObsidian();
      window.open(url, '_blank');
    } catch {
      alert('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  }

  // Add form state
  const [newType, setNewType] = useState<SourceType>('hackernews');
  const [newName, setNewName] = useState('');
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newFilters, setNewFilters] = useState('');
  const [newConfigSort, setNewConfigSort] = useState('hot');
  const [newConfigLanguage, setNewConfigLanguage] = useState('');

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editIdentifier, setEditIdentifier] = useState('');
  const [editFilters, setEditFilters] = useState('');
  const [editPollInterval, setEditPollInterval] = useState(120);
  const [editConfigSort, setEditConfigSort] = useState('');
  const [editConfigLanguage, setEditConfigLanguage] = useState('');

  const typeConfig = SOURCE_FIELD_CONFIG[newType];

  function handleTypeChange(type: SourceType) {
    setNewType(type);
    setNewIdentifier('');
    setNewFilters('');
    setNewConfigSort('hot');
    setNewConfigLanguage('');
  }

  async function loadSources() {
    setLoading(true);
    try {
      const result = await fetchSources();
      setSources(result.items);
    } catch (err) {
      console.error('Failed to load sources:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSources();
  }, []);

  async function handleAddSource() {
    const filters = newFilters
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);
    const config: Record<string, string> = {};
    if (typeConfig.fields.configSort) config.sort = newConfigSort;
    if (typeConfig.fields.configLanguage && newConfigLanguage) config.language = newConfigLanguage;
    const data: CreateSourceRequest = {
      type: newType,
      name: newName,
      identifier: newIdentifier,
      ...(filters.length > 0 && { filters }),
      ...(Object.keys(config).length > 0 && { config }),
    };
    await createSource(data);
    setShowAdd(false);
    setNewName('');
    setNewIdentifier('');
    setNewFilters('');
    setNewConfigSort('hot');
    setNewConfigLanguage('');
    await loadSources();
  }

  async function handleToggleSource(source: Source) {
    await updateSource(source.sourceId, { enabled: !source.enabled });
    await loadSources();
  }

  async function handlePollSource(sourceId: string) {
    setPollingId(sourceId);
    try {
      await pollSource(sourceId);
    } catch (err) {
      console.error('Failed to poll source:', err);
    } finally {
      await loadSources();
      setPollingId(null);
    }
  }

  async function handleDeleteSource(sourceId: string) {
    if (!confirm('Delete this source?')) return;
    await deleteSource(sourceId);
    await loadSources();
  }

  function handleEditSource(source: Source) {
    setEditingId(source.sourceId);
    setEditName(source.name);
    setEditIdentifier(source.identifier);
    setEditFilters(source.filters.join(', '));
    setEditPollInterval(source.pollIntervalMinutes);
    setEditConfigSort(source.config.sort || '');
    setEditConfigLanguage(source.config.language || '');
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    const source = sources.find((s) => s.sourceId === editingId);
    if (!source) return;

    const editTypeConfig = SOURCE_FIELD_CONFIG[source.type];
    const filters = editFilters
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);
    const config: Record<string, string> = {};
    if (editTypeConfig.fields.configSort && editConfigSort) config.sort = editConfigSort;
    if (editTypeConfig.fields.configLanguage && editConfigLanguage) config.language = editConfigLanguage;

    await updateSource(editingId, {
      name: editName,
      identifier: editIdentifier,
      filters,
      pollIntervalMinutes: editPollInterval,
      ...(Object.keys(config).length > 0 && { config }),
    });
    setEditingId(null);
    await loadSources();
  }

  function renderEditForm(source: Source) {
    const editTypeConfig = SOURCE_FIELD_CONFIG[source.type];

    return (
      <div key={source.sourceId} className="card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">
            Editing: <span className="text-obsidian-accent">{source.name}</span>
          </h3>
          <span className="badge bg-obsidian-bg-tertiary text-obsidian-text-faint text-xs">
            {source.type}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">Name</label>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="input w-full text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">
              Poll interval (minutes)
            </label>
            <input
              type="number"
              value={editPollInterval}
              onChange={(e) => setEditPollInterval(Number(e.target.value))}
              min={15}
              className="input w-full text-sm"
            />
          </div>
        </div>

        {editTypeConfig.fields.identifier && (
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">
              {editTypeConfig.fields.identifier.label}
            </label>
            <input
              value={editIdentifier}
              onChange={(e) => setEditIdentifier(e.target.value)}
              placeholder={editTypeConfig.fields.identifier.placeholder}
              className="input w-full text-sm"
            />
          </div>
        )}

        {editTypeConfig.fields.configSort && (
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">
              {editTypeConfig.fields.configSort.label}
            </label>
            <select
              value={editConfigSort}
              onChange={(e) => setEditConfigSort(e.target.value)}
              className="input w-full text-sm"
            >
              {editTypeConfig.fields.configSort.options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {editTypeConfig.fields.configLanguage && (
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">
              {editTypeConfig.fields.configLanguage.label}
            </label>
            <input
              value={editConfigLanguage}
              onChange={(e) => setEditConfigLanguage(e.target.value)}
              placeholder={editTypeConfig.fields.configLanguage.placeholder}
              className="input w-full text-sm"
            />
          </div>
        )}

        {editTypeConfig.fields.filters && (
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">
              {editTypeConfig.fields.filters.label}
            </label>
            <input
              value={editFilters}
              onChange={(e) => setEditFilters(e.target.value)}
              placeholder={editTypeConfig.fields.filters.placeholder}
              className="input w-full text-sm"
            />
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={() => setEditingId(null)} className="btn-ghost text-sm">
            Cancel
          </button>
          <button
            onClick={handleSaveEdit}
            disabled={!editName}
            className="btn-primary text-sm disabled:opacity-50"
          >
            Save Changes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className="text-lg font-semibold">Data Sources</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void handleExport()}
            disabled={exporting}
            className="btn-ghost text-sm disabled:opacity-50"
          >
            {exporting ? 'Preparing\u2026' : '\u2B07 Export to Obsidian'}
          </button>
          <button onClick={() => setShowAdd(!showAdd)} className="btn-primary text-sm">
            + Add Source
          </button>
        </div>
      </div>

      {/* Add source form */}
      {showAdd && (
        <div className="card mb-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                Type
              </label>
              <select
                value={newType}
                onChange={(e) => handleTypeChange(e.target.value as SourceType)}
                className="input w-full text-sm"
              >
                {SOURCE_TYPES.filter((t) => t !== 'telegram').map((t) => (
                  <option key={t} value={t}>
                    {t === 'rss' ? 'RSS / XML Feed' : t === 'hackernews' ? 'Hacker News' : t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                Name
              </label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Claude AI Subreddit"
                className="input w-full text-sm"
              />
            </div>
          </div>

          {!typeConfig.implemented && (
            <p className="text-xs text-obsidian-warning">
              Polling for this source type is not yet implemented. You can save it for later.
            </p>
          )}

          {typeConfig.fields.identifier && (
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                {typeConfig.fields.identifier.label}
              </label>
              <input
                value={newIdentifier}
                onChange={(e) => setNewIdentifier(e.target.value)}
                placeholder={typeConfig.fields.identifier.placeholder}
                className="input w-full text-sm"
              />
            </div>
          )}

          {typeConfig.fields.configSort && (
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                {typeConfig.fields.configSort.label}
              </label>
              <select
                value={newConfigSort}
                onChange={(e) => setNewConfigSort(e.target.value)}
                className="input w-full text-sm"
              >
                {typeConfig.fields.configSort.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {typeConfig.fields.configLanguage && (
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                {typeConfig.fields.configLanguage.label}
              </label>
              <input
                value={newConfigLanguage}
                onChange={(e) => setNewConfigLanguage(e.target.value)}
                placeholder={typeConfig.fields.configLanguage.placeholder}
                className="input w-full text-sm"
              />
            </div>
          )}

          {typeConfig.fields.filters && (
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                {typeConfig.fields.filters.label}
              </label>
              <input
                value={newFilters}
                onChange={(e) => setNewFilters(e.target.value)}
                placeholder={typeConfig.fields.filters.placeholder}
                className="input w-full text-sm"
              />
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="btn-ghost text-sm">
              Cancel
            </button>
            <button
              onClick={handleAddSource}
              disabled={
                !newName ||
                (!!typeConfig.fields.identifier && !newIdentifier) ||
                (!!typeConfig.fields.filters && !typeConfig.fields.identifier && !newFilters)
              }
              className="btn-primary text-sm disabled:opacity-50"
            >
              Add Source
            </button>
          </div>
        </div>
      )}

      {/* Sources list */}
      {loading ? (
        <div className="py-8 text-center text-obsidian-text-muted">Loading...</div>
      ) : sources.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-obsidian-text-muted text-sm">No sources configured</p>
          <p className="text-obsidian-text-faint text-xs mt-2">
            Add a data source to start collecting insights
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((source) =>
            editingId === source.sourceId ? (
              renderEditForm(source)
            ) : (
              <div key={source.sourceId} className="card flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                <div className="flex items-center gap-3 md:contents">
                  {/* Enable toggle */}
                  <button
                    onClick={() => handleToggleSource(source)}
                    className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${
                      source.enabled ? 'bg-obsidian-success' : 'bg-obsidian-surface'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                        source.enabled ? 'left-5' : 'left-0.5'
                      }`}
                    />
                  </button>

                  {/* Source info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium">{source.name}</h3>
                      <span className="badge bg-obsidian-bg-tertiary text-obsidian-text-faint text-xs">
                        {source.type}
                      </span>
                    </div>
                    <p className="text-xs text-obsidian-text-muted mt-0.5">
                      {source.identifier}
                      {source.filters.length > 0 && (
                        <span className="ml-2 text-obsidian-text-faint">
                          Filters: {source.filters.join(', ')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 md:contents">
                  {/* Last polled */}
                  <div className="text-left md:text-right flex-1 md:flex-none">
                    {source.lastPolledAt ? (
                      <p className="text-xs text-obsidian-text-faint">
                        Last polled: {new Date(source.lastPolledAt).toLocaleString()}
                      </p>
                    ) : (
                      <p className="text-xs text-obsidian-text-faint">Never polled</p>
                    )}
                    {source.lastError && (
                      <p className="text-xs text-obsidian-error mt-0.5 truncate max-w-48">
                        {source.lastError}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditSource(source)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-obsidian-text-faint hover:text-obsidian-accent transition-colors text-sm"
                      title="Edit source"
                    >
                      &#x270E;
                    </button>
                    <button
                      onClick={() => handlePollSource(source.sourceId)}
                      disabled={pollingId === source.sourceId}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-obsidian-text-faint hover:text-obsidian-text transition-colors text-sm disabled:opacity-50"
                      title="Poll now"
                    >
                      {pollingId === source.sourceId ? (
                        <span className="inline-block animate-spin">&orarr;</span>
                      ) : (
                        '\u21BB'
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteSource(source.sourceId)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-obsidian-text-faint hover:text-obsidian-error transition-colors text-sm"
                      title="Delete source"
                    >
                      &#x2715;
                    </button>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
